import * as React from 'react';

import { Monitor, Smartphone } from 'lucide-react';

import { useIsMobile } from '@/hooks/use-mobile';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export type DevicePreviewId =
  | 'full'
  | 'iphone-se'
  | 'iphone-15'
  | 'iphone-15-pro-max'
  | 'ipad-mini'
  | 'ipad-pro-11'
  | 'ipad-pro-13'
  | 'pixel-8'
  | 'galaxy-s23';

export type DevicePreviewKind = 'desktop' | 'phone' | 'tablet';

type DevicePreset = {
  id: DevicePreviewId;
  label: string;
  kind: DevicePreviewKind;
  width: number;
  height: number;
};

const DEVICE_PRESETS: DevicePreset[] = [
  { id: 'full', label: 'Full width', kind: 'desktop', width: 0, height: 0 },

  { id: 'iphone-se', label: 'iPhone SE (375×667)', kind: 'phone', width: 375, height: 667 },
  { id: 'iphone-15', label: 'iPhone 15 (390×844)', kind: 'phone', width: 390, height: 844 },
  { id: 'iphone-15-pro-max', label: 'iPhone 15 Pro Max (430×932)', kind: 'phone', width: 430, height: 932 },
  { id: 'pixel-8', label: 'Pixel 8 (412×915)', kind: 'phone', width: 412, height: 915 },
  { id: 'galaxy-s23', label: 'Galaxy S23 (360×780)', kind: 'phone', width: 360, height: 780 },

  { id: 'ipad-mini', label: 'iPad mini (768×1024)', kind: 'tablet', width: 768, height: 1024 },
  { id: 'ipad-pro-11', label: 'iPad Pro 11" (834×1194)', kind: 'tablet', width: 834, height: 1194 },
  { id: 'ipad-pro-13', label: 'iPad Pro 13" (1032×1376)', kind: 'tablet', width: 1032, height: 1376 },
];

function getDevicePreset(id: DevicePreviewId): DevicePreset {
  return DEVICE_PRESETS.find((preset) => preset.id === id) ?? DEVICE_PRESETS[0];
}

function getKindForDevice(id: DevicePreviewId): DevicePreviewKind {
  return getDevicePreset(id).kind;
}

function getDefaultDeviceForKind(kind: DevicePreviewKind): DevicePreviewId {
  if (kind === 'desktop') return 'full';
  const preset = DEVICE_PRESETS.find((entry) => entry.kind === kind);
  return preset?.id ?? 'full';
}

export function DevicePreviewControls({
  kind,
  onKindChange,
  value,
  onChange,
}: {
  kind: DevicePreviewKind;
  onKindChange: (value: DevicePreviewKind) => void;
  value: DevicePreviewId;
  onChange: (value: DevicePreviewId) => void;
}) {
  const isMobile = useIsMobile();
  const filteredPresets = DEVICE_PRESETS.filter((preset) => preset.kind === kind);
  const hasCurrentDevice = filteredPresets.some((preset) => preset.id === value);

  React.useEffect(() => {
    if (hasCurrentDevice) return;
    onChange(getDefaultDeviceForKind(kind));
  }, [hasCurrentDevice, kind, onChange]);

  if (isMobile) return null;

  return (
    <div className="mb-4 hidden md:flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card/40 px-3 py-2">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Monitor className="h-4 w-4" />
        <span className="font-medium text-foreground">Device preview</span>
        <span className="hidden lg:inline">(desktop only)</span>
      </div>

      <div className="flex items-center gap-2">
        <Smartphone className="h-4 w-4 text-muted-foreground" />
        <Select
          value={kind}
          onValueChange={(next) => {
            const nextKind = next as DevicePreviewKind;
            onKindChange(nextKind);
            if (getKindForDevice(value) !== nextKind) {
              onChange(getDefaultDeviceForKind(nextKind));
            }
          }}
        >
          <SelectTrigger className="h-8 w-[140px] bg-card/70 text-xs">
            <SelectValue placeholder="View" />
          </SelectTrigger>
          <SelectContent className="bg-card/90">
            <SelectItem value="desktop">Desktop</SelectItem>
            <SelectItem value="phone">Phone</SelectItem>
            <SelectItem value="tablet">Tablet</SelectItem>
          </SelectContent>
        </Select>

        {kind !== 'desktop' ? (
          <Select value={value} onValueChange={(next) => onChange(next as DevicePreviewId)}>
            <SelectTrigger className="h-8 w-[240px] bg-card/70 text-xs">
              <SelectValue placeholder="Choose device" />
            </SelectTrigger>
            <SelectContent className="bg-card/90">
              {filteredPresets.map((preset) => (
                <SelectItem key={preset.id} value={preset.id}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="text-xs text-muted-foreground">Full width</span>
        )}
      </div>
    </div>
  );
}

export function DevicePreviewShell({
  device,
  children,
}: {
  device: DevicePreviewId;
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();
  const preset = getDevicePreset(device);

  if (device === 'full' || isMobile) {
    return <>{children}</>;
  }

  return (
    <div className="mx-auto w-full md:flex md:justify-center">
      <div
        className="w-full md:rounded-xl md:border md:border-border/60 md:bg-background md:overflow-y-auto"
        style={{
          maxWidth: preset.width,
          height: preset.height,
        }}
      >
        <div className="px-0">{children}</div>
      </div>
    </div>
  );
}

export function DevicePreviewFrame({
  device,
  src,
  title,
}: {
  device: DevicePreviewId;
  src: string;
  title?: string;
}) {
  const preset = getDevicePreset(device);
  if (preset.id === 'full') return null;

  return (
    <div className="w-full flex justify-center">
      <div
        className="rounded-xl border border-border/60 bg-background overflow-hidden"
        style={{
          width: preset.width,
          height: preset.height,
          maxWidth: '100%',
        }}
      >
        <iframe
          title={title ?? 'Device preview'}
          src={src}
          className="h-full w-full"
          style={{ border: 0 }}
        />
      </div>
    </div>
  );
}
